import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Plus, GripVertical, Pencil, Trash2, Trophy, XCircle, Settings2 } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useDealTypes, useCreateDealType, useUpdateDealType, useDeleteDealType } from "@/hooks/use-deal-types"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import type { DealPhase, DealType } from "@/lib/types"

// ── Phase Form Dialog ────────────────────────────────────────────

interface PhaseFormData {
  name: string
  probability: number
  color: string
  is_won: boolean
  is_lost: boolean
  required_fields: string[]
}

const PHASE_COLORS = [
  "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#F97316",
  "#22C55E", "#14B8A6", "#06B6D4", "#6366F1", "#EF4444",
]

function PhaseFormDialog({
  open,
  onOpenChange,
  phase,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  phase: PhaseFormData | null
  onSave: (data: PhaseFormData) => void
}) {
  const [name, setName] = useState(phase?.name ?? "")
  const [probability, setProbability] = useState(phase?.probability ?? 0)
  const [color, setColor] = useState(phase?.color ?? "#3B82F6")
  const [isWon, setIsWon] = useState(phase?.is_won ?? false)
  const [isLost, setIsLost] = useState(phase?.is_lost ?? false)

  const inputClass = "h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none"

  const handleSave = () => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      probability,
      color,
      is_won: isWon,
      is_lost: isLost,
      required_fields: phase?.required_fields ?? [],
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{phase ? "Edytuj etap" : "Nowy etap"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Nazwa *
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputClass} border-input text-foreground`}
              placeholder="np. Kwalifikacja"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Prawdopodobieństwo (%)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={probability}
              onChange={(e) => setProbability(Number(e.target.value))}
              className={`${inputClass} border-input text-foreground`}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Kolor
            </label>
            <div className="flex flex-wrap gap-2">
              {PHASE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    color === c ? "border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={isWon}
                onChange={(e) => {
                  setIsWon(e.target.checked)
                  if (e.target.checked) setIsLost(false)
                }}
                className="rounded"
              />
              Etap wygranej
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={isLost}
                onChange={(e) => {
                  setIsLost(e.target.checked)
                  if (e.target.checked) setIsWon(false)
                }}
                className="rounded"
              />
              Etap przegranej
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={!name.trim()}>
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Sortable Phase Row ───────────────────────────────────────────

function SortablePhaseRow({
  phase,
  onEdit,
  onDelete,
}: {
  phase: DealPhase
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors"
      {...attributes}
    >
      <button
        {...listeners}
        className="cursor-grab touch-none rounded p-0.5 hover:bg-muted text-muted-foreground"
      >
        <GripVertical size={16} />
      </button>
      <div
        className="h-4 w-4 shrink-0 rounded-full"
        style={{ backgroundColor: phase.color }}
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground">
          {phase.name}
        </span>
      </div>
      <span className="text-xs font-mono tabular-nums text-muted-foreground">
        {phase.probability}%
      </span>
      {phase.is_won && (
        <Badge variant="outline" className="gap-1 text-xs border-green-300 text-green-700">
          <Trophy size={10} /> Wygrana
        </Badge>
      )}
      {phase.is_lost && (
        <Badge variant="outline" className="gap-1 text-xs border-red-300 text-red-700">
          <XCircle size={10} /> Przegrana
        </Badge>
      )}
      {phase.automations.length > 0 && (
        <Badge variant="secondary" className="text-xs">
          {phase.automations.length} aut.
        </Badge>
      )}
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="rounded p-1.5 transition-colors hover:bg-muted text-muted-foreground"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1.5 transition-colors hover:bg-black/5 text-destructive"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Deal Type Editor ─────────────────────────────────────────────

function DealTypeEditor({ dealType }: { dealType: DealType }) {
  const { t } = useTranslation()
  const updateMutation = useUpdateDealType()
  const deleteMutation = useDeleteDealType()
  const [phases, setPhases] = useState<DealPhase[]>(dealType.phases)
  const [editingPhase, setEditingPhase] = useState<{ index: number; data: PhaseFormData } | null>(null)
  const [addingPhase, setAddingPhase] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [typeName, setTypeName] = useState(dealType.name)
  const [hasChanges, setHasChanges] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = phases.findIndex((p) => p.id === active.id)
    const newIndex = phases.findIndex((p) => p.id === over.id)

    const reordered = arrayMove(phases, oldIndex, newIndex).map((p, idx) => ({
      ...p,
      sort_order: idx,
    }))
    setPhases(reordered)
    setHasChanges(true)
  }

  const handleAddPhase = (data: PhaseFormData) => {
    const newPhase: DealPhase = {
      id: `new_${Date.now()}`,
      name: data.name,
      sort_order: phases.length,
      probability: data.probability,
      color: data.color,
      is_won: data.is_won,
      is_lost: data.is_lost,
      required_fields: data.required_fields,
      automations: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setPhases([...phases, newPhase])
    setHasChanges(true)
  }

  const handleEditPhase = (index: number, data: PhaseFormData) => {
    const updated = [...phases]
    updated[index] = { ...updated[index], ...data }
    setPhases(updated)
    setHasChanges(true)
  }

  const handleDeletePhase = (index: number) => {
    if (!confirm("Czy na pewno chcesz usunąć ten etap?")) return
    const updated = phases.filter((_, i) => i !== index).map((p, idx) => ({
      ...p,
      sort_order: idx,
    }))
    setPhases(updated)
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      const phasesPayload = phases.map((p, idx) => ({
        id: p.id.startsWith("new_") ? undefined : p.id,
        name: p.name,
        sort_order: idx,
        probability: p.probability,
        color: p.color,
        is_won: p.is_won,
        is_lost: p.is_lost,
        required_fields: p.required_fields,
      }))

      await updateMutation.mutateAsync({
        id: dealType.id,
        data: {
          name: typeName,
          phases: phasesPayload,
        },
      })
      setHasChanges(false)
      toast.success("Pipeline zaktualizowany")
    } catch {
      toast.error("Nie udało się zapisać zmian")
    }
  }

  const handleDeleteType = async () => {
    if (!confirm("Czy na pewno chcesz usunąć ten pipeline? Tej operacji nie można cofnąć.")) return
    try {
      await deleteMutation.mutateAsync(dealType.id)
      toast.success("Pipeline usunięty")
    } catch {
      toast.error("Nie udało się usunąć pipeline'u")
    }
  }

  const inputClass = "h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none"

  return (
    <div className="rounded-lg border border-border">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          {isEditingName ? (
            <input
              value={typeName}
              onChange={(e) => {
                setTypeName(e.target.value)
                setHasChanges(true)
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setIsEditingName(false)
              }}
              className={`${inputClass} border-input text-foreground max-w-[200px]`}
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="flex items-center gap-2 text-base font-semibold hover:opacity-70 text-foreground"
            >
              {typeName}
              <Pencil size={14} className="text-muted-foreground opacity-60" />
            </button>
          )}
          {dealType.is_default && (
            <Badge variant="secondary" className="text-xs">Domyślny</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!dealType.is_default && (
            <Button variant="ghost" size="sm" onClick={handleDeleteType} className="text-red-600 hover:text-red-700">
              <Trash2 size={14} /> {t("common.delete")}
            </Button>
          )}
        </div>
      </div>

      {/* Phases list */}
      <div className="space-y-2 p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={phases.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            {phases.map((phase, index) => (
              <SortablePhaseRow
                key={phase.id}
                phase={phase}
                onEdit={() =>
                  setEditingPhase({
                    index,
                    data: {
                      name: phase.name,
                      probability: phase.probability,
                      color: phase.color,
                      is_won: phase.is_won,
                      is_lost: phase.is_lost,
                      required_fields: phase.required_fields,
                    },
                  })
                }
                onDelete={() => handleDeletePhase(index)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button
          onClick={() => setAddingPhase(true)}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-input py-2.5 text-sm transition-colors hover:border-solid text-muted-foreground"
        >
          <Plus size={16} /> Dodaj etap
        </button>
      </div>

      {/* Save bar */}
      {hasChanges && (
        <div className="flex items-center justify-end gap-3 border-t border-border px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPhases(dealType.phases)
              setTypeName(dealType.name)
              setHasChanges(false)
            }}
          >
            Cofnij zmiany
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </div>
      )}

      {/* Phase form dialogs */}
      <PhaseFormDialog
        open={addingPhase}
        onOpenChange={setAddingPhase}
        phase={null}
        onSave={handleAddPhase}
      />

      {editingPhase && (
        <PhaseFormDialog
          open={true}
          onOpenChange={() => setEditingPhase(null)}
          phase={editingPhase.data}
          onSave={(data) => handleEditPhase(editingPhase.index, data)}
        />
      )}
    </div>
  )
}

// ── Create Deal Type Dialog ──────────────────────────────────────

function CreateDealTypeDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [name, setName] = useState("")
  const createMutation = useCreateDealType()
  const inputClass = "h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none"

  const handleCreate = async () => {
    if (!name.trim()) return
    try {
      await createMutation.mutateAsync({ name: name.trim() })
      toast.success("Pipeline utworzony")
      setName("")
      onOpenChange(false)
    } catch {
      toast.error("Nie udało się utworzyć pipeline'u")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nowy pipeline</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            Nazwa
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${inputClass} border-input text-foreground`}
            placeholder="np. Enterprise Sales"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate()
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button onClick={handleCreate} disabled={!name.trim() || createMutation.isPending}>
            {createMutation.isPending ? "Tworzenie..." : "Utwórz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Page ────────────────────────────────────────────────────

export function DealPhasesPage() {
  const { data, isLoading } = useDealTypes()
  const [showCreate, setShowCreate] = useState(false)

  const dealTypes = data?.data ?? []

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[800px] space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[800px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings2 size={22} className="text-primary" />
          <div>
            <h1 className="page-title">
              Pipeline sprzedaży
            </h1>
            <p className="text-sm text-muted-foreground">
              Konfiguruj etapy pipeline'u sprzedażowego
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Nowy pipeline
        </Button>
      </div>

      {dealTypes.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground"
        >
          <Settings2 size={40} />
          <p className="text-sm">Nie masz jeszcze żadnych pipeline'ów</p>
          <Button variant="outline" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Utwórz pierwszy pipeline
          </Button>
        </div>
      )}

      {dealTypes.map((dt) => (
        <DealTypeEditor key={dt.id} dealType={dt} />
      ))}

      <CreateDealTypeDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  )
}
